import { pipeline, env } from '@huggingface/transformers';

// Model weights are fetched once from the Hugging Face CDN and cached by the
// browser (IndexedDB/Cache Storage); after that, inference runs fully offline
// and no audio ever leaves the device.
env.allowLocalModels = false;

export const WHISPER_MODELS = {
  fast: 'Xenova/whisper-tiny.en',
  balanced: 'Xenova/whisper-base.en',
  accurate: 'Xenova/whisper-small.en',
};
let currentModel = null;
let currentPipeline = null;
let transcriptionQueue = Promise.resolve();

async function loadLocalWhisper(onProgress, quality) {
  const model = WHISPER_MODELS[quality] || WHISPER_MODELS.balanced;
  if (currentModel !== model || !currentPipeline) {
    if (currentPipeline) await currentPipeline.dispose();
    currentPipeline = null;
    currentModel = null;
    currentPipeline = await pipeline('automatic-speech-recognition', model, {
      dtype: 'q8',
      // Avoid the ONNX Runtime quantization fusion bug on shared embeddings.
      session_options: { graphOptimizationLevel: 'basic' },
      progress_callback: onProgress,
    });
    currentModel = model;
  }
  return currentPipeline;
}

async function decodeToMono16k(blob) {
  const arrayBuffer = await blob.arrayBuffer();
  const audioCtx = new AudioContext();
  let decoded;
  try {
    decoded = await audioCtx.decodeAudioData(arrayBuffer);
  } finally {
    audioCtx.close();
  }

  const targetRate = 16000;
  const offline = new OfflineAudioContext(1, Math.ceil(decoded.duration * targetRate), targetRate);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();
  return rendered.getChannelData(0);
}

export function transcribeLocally(blob, onProgress, quality = 'balanced') {
  // Switching models must wait for previous inference before releasing its memory.
  const task = transcriptionQueue.then(async () => {
    const asr = await loadLocalWhisper(onProgress, quality);
    const samples = await decodeToMono16k(blob);
    const result = await asr(samples, { chunk_length_s: 30, stride_length_s: 5 });
    return (result?.text || '').trim();
  });
  transcriptionQueue = task.catch(() => {});
  return task;
}
